// src/utils/productCardImage.js
//
// Генерация PNG-картинки для шаринга товара.
// Два формата:
//   - 'square'   1080×1080 — для мессенджеров, постов IG, фид
//   - 'vertical' 1080×1920 — для Instagram Stories / Reels-обложек / TikTok
//
// Возвращает: base64 PNG (без префикса data:image/png;base64,).

async function loadImage(url) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image load failed: ' + url));
    img.src = url;
  });
}

function wrapText(ctx, text, maxWidth) {
  const words = String(text).split(/\s+/);
  const lines = [];
  let current = '';
  for (const w of words) {
    const test = current ? current + ' ' + w : w;
    const width = ctx.measureText(test).width;
    if (width > maxWidth && current) {
      lines.push(current);
      current = w;
    } else {
      current = test;
    }
  }
  if (current) lines.push(current);
  return lines;
}

function ellipsize(ctx, text, maxWidth) {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let s = text;
  while (s.length > 0 && ctx.measureText(s + '…').width > maxWidth) {
    s = s.slice(0, -1);
  }
  return s + '…';
}

function drawImageCover(ctx, img, x, y, w, h) {
  const ir = img.width / img.height;
  const dr = w / h;
  let sx = 0, sy = 0, sw = img.width, sh = img.height;
  if (ir > dr) {
    sw = img.height * dr;
    sx = (img.width - sw) / 2;
  } else {
    sh = img.width / dr;
    sy = (img.height - sh) / 2;
  }
  ctx.drawImage(img, sx, sy, sw, sh, x, y, w, h);
}

function drawPlaceholder(ctx, x, y, w, h, emojiSize = 180) {
  const grad = ctx.createLinearGradient(x, y, x + w, y + h);
  grad.addColorStop(0, '#e8eaf6');
  grad.addColorStop(1, '#f3e8ff');
  ctx.fillStyle = grad;
  ctx.fillRect(x, y, w, h);
  ctx.fillStyle = '#c7c9d1';
  ctx.font = `${emojiSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('📦', x + w / 2, y + h / 2);
}

// ───────────────────────────────────────────────────────────────
// Общая логика рендера
// ───────────────────────────────────────────────────────────────
//
// Layout:
//   - Фоновая зона на всю площадь — мягкий брендовый градиент (для вертикали даёт
//     красивые поля сверху/снизу, для квадрата — почти не виден)
//   - Фото-зона в центре с белой подложкой и небольшим скруглением
//   - Под фото — белая панель с названием/ценой/магазином
//
// Параметры для каждого формата задаются через layout-объект.

async function renderCard({ width, height, photoUrl, name, price, shopName, layout }) {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');

  // 1. Фоновый градиент на всю площадь
  const bgGrad = ctx.createLinearGradient(0, 0, 0, height);
  bgGrad.addColorStop(0, '#eef0fb');
  bgGrad.addColorStop(1, '#f5edff');
  ctx.fillStyle = bgGrad;
  ctx.fillRect(0, 0, width, height);

  // Координаты карточки внутри (для квадрата = вся площадь; для вертикали — с полями)
  const cardX = layout.cardX;
  const cardY = layout.cardY;
  const cardW = layout.cardW;
  const cardH = layout.cardH;
  const photoH = layout.photoH;
  const panelH = cardH - photoH;
  const padX = layout.padX;
  const radius = layout.radius;

  // 2. Белая подложка карточки со скругленными углами
  ctx.save();
  roundRect(ctx, cardX, cardY, cardW, cardH, radius);
  ctx.clip();

  // 2a. Фото-зона
  if (photoUrl) {
    try {
      const img = await loadImage(photoUrl);
      drawImageCover(ctx, img, cardX, cardY, cardW, photoH);
    } catch (e) {
      drawPlaceholder(ctx, cardX, cardY, cardW, photoH, layout.placeholderSize);
    }
  } else {
    drawPlaceholder(ctx, cardX, cardY, cardW, photoH, layout.placeholderSize);
  }

  // 2b. Панель снизу (белая)
  const panelY = cardY + photoH;
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(cardX, panelY, cardW, panelH);

  // Лёгкая тень-разделитель
  const sepGrad = ctx.createLinearGradient(0, panelY, 0, panelY + 20);
  sepGrad.addColorStop(0, 'rgba(0,0,0,0.08)');
  sepGrad.addColorStop(1, 'rgba(0,0,0,0)');
  ctx.fillStyle = sepGrad;
  ctx.fillRect(cardX, panelY, cardW, 20);

  ctx.restore(); // снимаем clip

  // 3. Текст в панели
  const maxTextW = cardW - padX * 2;
  const textLeft = cardX + padX;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'top';

  // Название
  ctx.fillStyle = '#1a1d23';
  ctx.font = `bold ${layout.nameFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif`;
  const nameLines = wrapText(ctx, name || '', maxTextW).slice(0, 2);
  if (nameLines.length === 2 && wrapText(ctx, name, maxTextW).length > 2) {
    nameLines[1] = ellipsize(ctx, nameLines[1], maxTextW);
  }
  let cy = panelY + layout.panelPadTop;
  for (const line of nameLines) {
    ctx.fillText(line, textLeft, cy);
    cy += layout.nameLineHeight;
  }

  // Цена
  ctx.fillStyle = '#6366f1';
  ctx.font = `bold ${layout.priceFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif`;
  const priceText = ellipsize(ctx, String(price || ''), maxTextW);
  const priceY = Math.min(cy + layout.priceGap, panelY + panelH - layout.shopReservedSpace);
  ctx.fillText(priceText, textLeft, priceY);

  // Магазин (внизу панели)
  if (shopName) {
    ctx.fillStyle = '#5b6372';
    ctx.font = `${layout.shopFontSize}px -apple-system, BlinkMacSystemFont, "Segoe UI", system-ui, sans-serif`;
    const shopText = ellipsize(ctx, '📍 ' + shopName, maxTextW);
    ctx.fillText(shopText, textLeft, panelY + panelH - layout.shopBottomOffset);
  }

  const dataUrl = canvas.toDataURL('image/png');
  return dataUrl.split(',', 2)[1];
}

// Скругленный прямоугольник
function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}

// ───────────────────────────────────────────────────────────────
// Публичные функции — два формата
// ───────────────────────────────────────────────────────────────

// Квадрат 1080×1080 — для WhatsApp, Telegram, IG-feed
export async function generateProductCardSquare(params) {
  const W = 1080, H = 1080;
  return renderCard({
    ...params,
    width: W,
    height: H,
    layout: {
      cardX: 0, cardY: 0, cardW: W, cardH: H,
      photoH: 760,
      padX: 48,
      radius: 0,
      placeholderSize: 180,
      nameFontSize: 52,
      nameLineHeight: 58,
      priceFontSize: 72,
      shopFontSize: 32,
      panelPadTop: 32,
      priceGap: 8,
      shopReservedSpace: 130,
      shopBottomOffset: 56,
    },
  });
}

// Вертикаль 1080×1920 — для Instagram Stories
export async function generateProductCardVertical(params) {
  const W = 1080, H = 1920;
  // Карточка по центру, ~80% высоты, с полями сверху/снизу = красивые поля Stories
  const cardW = 960;
  const cardH = 1400;
  const cardX = (W - cardW) / 2;
  const cardY = (H - cardH) / 2;
  return renderCard({
    ...params,
    width: W,
    height: H,
    layout: {
      cardX, cardY, cardW, cardH,
      photoH: 980,             // фото 70% карточки
      padX: 56,
      radius: 32,              // скругленные углы
      placeholderSize: 220,
      nameFontSize: 60,
      nameLineHeight: 70,
      priceFontSize: 84,
      shopFontSize: 36,
      panelPadTop: 44,
      priceGap: 12,
      shopReservedSpace: 150,
      shopBottomOffset: 68,
    },
  });
}

// Совместимость со старым API — старый код мог вызывать generateProductCardImage(...)
// Делаем алиас на квадрат, чтобы ничего не сломать.
export const generateProductCardImage = generateProductCardSquare;
